import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, merge, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { DsBreakpoints } from '@levelaccess/design-system';
import { WindowService } from '../../services/window.service';
import { BrowserLocalStorageService, LOCAL_STORAGE } from '../../services/browser-local-storage.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { UserService } from '../../services/user.service';
import { FeatureFlagCollection } from '../../../../shared/interfaces/feature-flag.interface';
import { CommonUtility } from '../../utility/common.utility';

@Injectable({
  providedIn: 'root',
})
export class AiChatStateService implements OnDestroy {
  private readonly isChatOpenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly isExpandedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly showChatHistorySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly suggestionsDisabledSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private subscriptions: Subscription = new Subscription();
  private enhancementsEnabled: boolean = false;
  private selectedChatPage: number = 1;
  private draftMessage: string = '';

  public isChatOpen$: Observable<boolean> = this.isChatOpenSubject.asObservable();
  public showChatHistory$: Observable<boolean> = this.showChatHistorySubject.asObservable();
  public suggestionsDisabled$: Observable<boolean> = this.suggestionsDisabledSubject.asObservable();

  public showAiChatPanel$: Observable<boolean> = combineLatest([
    this.isChatOpenSubject,
    this.windowService.observeBreakpoint(DsBreakpoints.Medium),
  ]).pipe(map(([isOpen, isMobile]: [boolean, boolean]) => isOpen && !isMobile));

  public showExpandedPanel$: Observable<boolean> = combineLatest([
    this.isExpandedSubject,
    this.windowService.observeBreakpoint(DsBreakpoints.Medium),
  ]).pipe(
    tap(([expanded, isMobile]: [boolean, boolean]) => {
      if (isMobile && expanded) {
        this.isExpandedSubject.next(false);
      }
    }),
    map(([expanded, isMobile]: [boolean, boolean]) => expanded && !isMobile),
  );

  constructor(
    private windowService: WindowService,
    private browserLocalStorageService: BrowserLocalStorageService,
    private featureFlagService: FeatureFlagService,
    private userService: UserService,
  ) {
    this.subscriptions.add(
      this.userService.isAuthenticated$
        .pipe(
          distinctUntilChanged(),
          filter((isAuthenticated: boolean) => !isAuthenticated),
        )
        .subscribe(() => {
          this.isChatOpenSubject.next(false);
          this.isExpandedSubject.next(false);
        }),
    );

    this.subscriptions.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.verticalNavEnhancements, false)
        .pipe(
          map(Boolean),
          distinctUntilChanged(),
          tap((enabled: boolean) => {
            this.enhancementsEnabled = enabled;
            if (enabled) {
              const savedOpen: boolean | null = this.browserLocalStorageService.getItem(LOCAL_STORAGE.AI_CHAT_PANEL_OPEN);
              const savedExpanded: boolean | null = this.browserLocalStorageService.getItem(LOCAL_STORAGE.AI_CHAT_PANEL_EXPANDED);
              if (savedOpen !== null) {
                this.isChatOpenSubject.next(savedOpen);
              }
              if (savedExpanded !== null) {
                this.isExpandedSubject.next(savedExpanded);
              }
            }
          }),
          switchMap((enabled: boolean) =>
            enabled
              ? merge(
                  this.browserLocalStorageService
                    .observeKey$<boolean>(LOCAL_STORAGE.AI_CHAT_PANEL_OPEN)
                    .pipe(tap((value: boolean | null) => this.isChatOpenSubject.next(value ?? false))),
                  this.browserLocalStorageService
                    .observeKey$<boolean>(LOCAL_STORAGE.AI_CHAT_PANEL_EXPANDED)
                    .pipe(tap((value: boolean | null) => this.isExpandedSubject.next(value ?? false))),
                )
              : EMPTY,
          ),
        )
        .subscribe(),
    );
  }

  public toggleAiChat(): void {
    const next = !this.isChatOpenSubject.value;
    if (next) {
      this.showChatHistorySubject.next(false);
    } else {
      this.isExpandedSubject.next(false);
    }
    this.isChatOpenSubject.next(next);
    if (this.enhancementsEnabled) {
      this.browserLocalStorageService.setItem(LOCAL_STORAGE.AI_CHAT_PANEL_OPEN, next);
      if (!next) {
        this.browserLocalStorageService.setItem(LOCAL_STORAGE.AI_CHAT_PANEL_EXPANDED, false);
      }
    }
  }

  public closeAiChatPanel(options?: { moveFocusToTrigger?: boolean }): void {
    this.isChatOpenSubject.next(false);
    this.isExpandedSubject.next(false);
    if (this.enhancementsEnabled) {
      this.browserLocalStorageService.setItem(LOCAL_STORAGE.AI_CHAT_PANEL_OPEN, false);
      this.browserLocalStorageService.setItem(LOCAL_STORAGE.AI_CHAT_PANEL_EXPANDED, false);
    }

    if (options?.moveFocusToTrigger !== false) {
      const triggerButton: HTMLElement | null = this.getFirstVisibleAiChatTriggerButton();
      if (triggerButton !== null) {
        CommonUtility.setFocusToElement(null, triggerButton);
      }
    }
  }

  private getFirstVisibleAiChatTriggerButton(): HTMLElement | null {
    const triggerButtons: HTMLElement[] = Array.from(document.querySelectorAll<HTMLElement>('app-ai-chat-button button'));
    return triggerButtons.find((button: HTMLElement) => button.offsetParent !== null) ?? null;
  }

  public expandAIChatPanel(): void {
    this.isExpandedSubject.next(true);
    if (this.enhancementsEnabled) {
      this.browserLocalStorageService.setItem(LOCAL_STORAGE.AI_CHAT_PANEL_EXPANDED, true);
    }
  }

  public collapseAIChatPanel(): void {
    this.isExpandedSubject.next(false);
    if (this.enhancementsEnabled) {
      this.browserLocalStorageService.setItem(LOCAL_STORAGE.AI_CHAT_PANEL_EXPANDED, false);
    }
  }

  public toggleChatHistory(): void {
    this.showChatHistorySubject.next(!this.showChatHistorySubject.value);
  }

  public hideChatHistory(): void {
    this.showChatHistorySubject.next(false);
  }

  public getSelectedChatPage(): number {
    return this.selectedChatPage;
  }

  public setSelectedChatPage(page: number): void {
    this.selectedChatPage = page;
  }

  public getDraftMessage(): string {
    return this.draftMessage;
  }

  public setDraftMessage(message: string): void {
    this.draftMessage = message;
  }

  public setSuggestionsDisabled(disabled: boolean): void {
    this.suggestionsDisabledSubject.next(disabled);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
